import { Link } from 'react-router-dom';
import { Building2, Check, Smartphone, ArrowLeft, Users, FileText, Calculator, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';

const ToolCopropriete = () => {
  const features = [
    {
      icon: Users,
      title: "Gestion des copropriétaires",
      description: "Ajoutez facilement tous les membres et gérez leurs tantièmes."
    },
    {
      icon: Calculator,
      title: "Appels de fonds automatiques",
      description: "Calculez et envoyez les appels de charges en quelques clics."
    },
    {
      icon: FileText,
      title: "Procès-verbaux d'AG",
      description: "Créez et archivez vos PV d'assemblée générale."
    },
    {
      icon: Shield,
      title: "Conformité légale",
      description: "Respectez les obligations légales sans expertise juridique."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Gestion Copropriété Bénévole - Application Gratuite | G Division</title>
        <meta name="description" content="Application gratuite pour gérer votre copropriété en bénévole. Idéal pour les petites copropriétés sans syndic professionnel. Disponible sur Play Store." />
        <meta name="keywords" content="gérer copropriété bénévole, application copropriété gratuite, syndic bénévole, gestion copropriété sans syndic, logiciel copropriété gratuit" />
        <link rel="canonical" href="https://divisionimmobiliere.com/nos-outils/gestion-copropriete" />
        
        <script type="application/ld+json">
        {`{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Copropriété App",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Android, Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "EUR"
          },
          "description": "Application gratuite pour gérer votre copropriété en bénévole"
        }`}
        </script>
      </Helmet>

      <div className="pt-20" data-testid="tool-copropriete-page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <Link 
              to="/nos-outils" 
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="mr-2" size={18} />
              Retour aux outils
            </Link>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Building2 size={40} className="text-white" />
                  <div className="bg-white/10 rounded-sm px-3 py-1 flex items-center gap-2">
                    <Smartphone size={16} className="text-white" />
                    <span className="text-white text-sm">Play Store</span>
                  </div>
                </div>
                
                <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
                  Gérer sa Copropriété en Bénévole
                </h1>
                <p className="text-blue-100 text-lg leading-relaxed mb-8">
                  L'application gratuite qui simplifie la gestion de votre copropriété. 
                  Idéal pour les syndics bénévoles et les petites copropriétés.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="https://copropriete.app/" target="_blank" rel="noopener noreferrer">
                    <Button 
                      className="bg-white text-blue-600 hover:bg-blue-50 rounded-none px-8 py-4 uppercase tracking-wider text-sm font-semibold"
                    >
                      Accéder à l'Application
                    </Button>
                  </a>
                </div>
              </div>
              
              <div className="hidden lg:block">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8">
                  <img 
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600"
                    alt="Gestion copropriété"
                    className="w-full h-64 object-cover rounded-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-16">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-4">
                Fonctionnalités Principales
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Tout ce dont vous avez besoin pour gérer votre copropriété efficacement.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="text-center p-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="text-blue-600" size={28} />
                  </div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Use */}
        <section className="py-16 md:py-24 bg-[#f8f9fa]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#1e3a5f] mb-6">
                  Pourquoi Gérer sa Copropriété en Bénévole ?
                </h2>
                <div className="space-y-4">
                  {[
                    "Économisez les frais de syndic professionnel (1000€ à 3000€/an)",
                    "Gardez le contrôle total sur la gestion de votre immeuble",
                    "Idéal pour les petites copropriétés (moins de 10 lots)",
                    "Décisions plus rapides sans intermédiaire",
                    "Transparence totale sur les comptes"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <Check className="text-green-500 mt-1 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="bg-white p-8 shadow-lg">
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1e3a5f] mb-4">
                    Besoin d'Aide pour Créer votre Copropriété ?
                  </h3>
                  <p className="text-gray-600 mb-6">
                    G Division vous accompagne dans la création et la structuration 
                    de votre copropriété. De la division du bien au règlement de copropriété.
                  </p>
                  <Link to="/contact">
                    <Button className="bg-[#1e3a5f] text-white hover:bg-[#162a45] rounded-none w-full">
                      Demander un Accompagnement
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-blue-600">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-white mb-6">
              Prêt à Simplifier la Gestion de Votre Copropriété ?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Rejoignez les centaines de copropriétés qui utilisent notre application.
            </p>
            <a href="https://copropriete.app/" target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-white text-blue-600 hover:bg-blue-50 rounded-none px-10 py-4 uppercase tracking-wider text-sm font-semibold"
              >
                Commencer Gratuitement
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default ToolCopropriete;
